/**
 * 環境情報取得 (F3: 天候・月齢・日の出/日の入り・太陽高度)
 *
 * - 月齢: 簡易計算 (新月 2000-01-06 18:14 UTC 起点)
 * - 日の出/日の入り/南中時刻: NOAA 計算式の簡略版
 * - 太陽高度・方位: 同上
 * - 天候: Open-Meteo API (https://open-meteo.com, API key 不要・商用可)
 *
 * 撮影時に gps が取れていれば自動的に取得し、シーンに保存する。
 * オフライン or API 失敗時は計算可能なものだけ (月齢・日の出など) を保存。
 */

import { getCurrentShoot, updateCurrentShoot } from './scene.js';

const OPEN_METEO_BASE = 'https://api.open-meteo.com/v1/forecast';

export async function fetchEnvironment(gps, atMs) {
  if (!gps || !Number.isFinite(gps.lat) || !Number.isFinite(gps.lon)) return;
  const at = new Date(atMs || Date.now());
  const env = {
    moonPhase: calculateMoonPhase(at),
    moonIllumination: null,
    sunrise: null,
    sunset: null,
    solarNoon: null,
    sunAltitude: null,
    sunAzimuth: null,
    weather: null,
    fetchedAt: Date.now(),
  };
  // 月齢から照度概算
  env.moonIllumination = Math.round((1 - Math.cos(env.moonPhase * 2 * Math.PI)) * 50);

  // 日の出・日の入り・太陽位置
  const sun = calculateSunTimes(at, gps.lat, gps.lon);
  env.sunrise = sun.sunrise;
  env.sunset = sun.sunset;
  env.solarNoon = sun.solarNoon;
  env.sunAltitude = sun.altitude;
  env.sunAzimuth = sun.azimuth;

  // 天気を Open-Meteo で取得 (オプション、失敗時はスキップ)
  try {
    const res = await fetch(`${OPEN_METEO_BASE}?latitude=${gps.lat}&longitude=${gps.lon}&current=temperature_2m,relative_humidity_2m,weather_code,cloud_cover,wind_speed_10m`);
    if (res.ok) {
      const data = await res.json();
      const c = data?.current || {};
      env.weather = {
        tempC: c.temperature_2m,
        humidity: c.relative_humidity_2m,
        weatherCode: c.weather_code,
        cloudCover: c.cloud_cover,
        windSpeed: c.wind_speed_10m,
        description: weatherCodeToText(c.weather_code),
      };
    }
  } catch (e) {
    console.info('[env] weather fetch failed:', e?.message);
  }

  // 現在のシーンに反映 (現在の撮影と紐付ける)
  const shoot = getCurrentShoot();
  if (shoot && !shoot.environment) {
    updateCurrentShoot({ environment: env });
  }
  return env;
}

// --------------------------------------------------
// 月齢計算 (Conway algorithm 簡略版)
// 戻り値: 0.0 = 新月, 0.5 = 満月, 1.0 = 次の新月
// --------------------------------------------------
function calculateMoonPhase(date) {
  const SYNODIC_MONTH = 29.530588853;
  const NEW_MOON_REF = Date.UTC(2000, 0, 6, 18, 14);  // 2000-01-06 18:14 UTC = 既知の新月
  const days = (date.getTime() - NEW_MOON_REF) / (1000 * 60 * 60 * 24);
  let phase = (days % SYNODIC_MONTH) / SYNODIC_MONTH;
  if (phase < 0) phase += 1;
  return phase;
}

export function moonPhaseLabel(phase) {
  if (phase < 0.03 || phase > 0.97) return '新月';
  if (phase < 0.22) return '三日月 (上弦前)';
  if (phase < 0.28) return '上弦の月';
  if (phase < 0.47) return '十三夜 (上弦後)';
  if (phase < 0.53) return '満月';
  if (phase < 0.72) return '十六夜 (下弦前)';
  if (phase < 0.78) return '下弦の月';
  return '有明月 (下弦後)';
}

// --------------------------------------------------
// 太陽位置計算 (NOAA Solar Position Algorithm の簡略版)
// 精度: ±1° 程度 (実用十分)
// --------------------------------------------------
function calculateSunTimes(date, lat, lon) {
  const RAD = Math.PI / 180;
  const DEG = 180 / Math.PI;

  // ユリウス日
  const dayMs = 1000 * 60 * 60 * 24;
  const J2000 = Date.UTC(2000, 0, 1, 12, 0) / dayMs;
  const days = date.getTime() / dayMs - J2000;

  // 太陽の平均黄経・離角
  const M = (357.5291 + 0.98560028 * days) * RAD;  // 平均近点角
  const C = (1.9148 * Math.sin(M) + 0.0200 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M)) * RAD;
  const lambda = ((280.4665 + 0.98564736 * days) * RAD + C + Math.PI);  // 黄経
  const declination = Math.asin(Math.sin(lambda) * Math.sin(23.4397 * RAD));

  // 時角・南中
  const J0 = 0.0009;
  const lw = -lon * RAD;
  const n = Math.round(days - J0 - lw / (2 * Math.PI));
  const Jstar = J0 + lw / (2 * Math.PI) + n;
  const Mstar = (357.5291 + 0.98560028 * Jstar) * RAD;
  const Cstar = (1.9148 * Math.sin(Mstar) + 0.0200 * Math.sin(2 * Mstar)) * RAD;
  const Lstar = ((280.4665 + 0.98564736 * Jstar) * RAD + Cstar + Math.PI);
  const Jtransit = J2000 + Jstar + 0.0053 * Math.sin(Mstar) - 0.0069 * Math.sin(2 * Lstar);

  const phi = lat * RAD;
  const cosH = (Math.sin(-0.83 * RAD) - Math.sin(phi) * Math.sin(declination)) / (Math.cos(phi) * Math.cos(declination));
  let sunrise = null, sunset = null;
  if (cosH >= -1 && cosH <= 1) {
    const H = Math.acos(cosH) / (2 * Math.PI);
    sunset = new Date((Jtransit + H) * dayMs);
    sunrise = new Date((Jtransit - H) * dayMs);
  }
  const solarNoon = new Date(Jtransit * dayMs);

  // 現在の太陽高度・方位
  const hourAngle = (date.getTime() - solarNoon.getTime()) / (1000 * 60 * 60) * 15 * RAD;
  const altitude = Math.asin(Math.sin(phi) * Math.sin(declination) + Math.cos(phi) * Math.cos(declination) * Math.cos(hourAngle)) * DEG;
  const azimuth = (Math.atan2(Math.sin(hourAngle), Math.cos(hourAngle) * Math.sin(phi) - Math.tan(declination) * Math.cos(phi)) * DEG + 180) % 360;

  return {
    sunrise: sunrise?.toISOString() || null,
    sunset: sunset?.toISOString() || null,
    solarNoon: solarNoon.toISOString(),
    altitude: Math.round(altitude * 10) / 10,
    azimuth: Math.round(azimuth * 10) / 10,
  };
}

// --------------------------------------------------
// 太陽高度 → 撮影シーンの示唆
// --------------------------------------------------
export function sunAltitudeLabel(alt) {
  if (alt == null) return '—';
  if (alt < -6) return '夜間 (天文薄明前/後)';
  if (alt < 0) return '薄明 (Civil/Nautical Twilight)';
  if (alt < 6) return 'マジックアワー (低い太陽)';
  if (alt < 25) return '斜光 (側光・サイド光に最適)';
  if (alt < 50) return '通常光 (順光ベース)';
  if (alt < 75) return '高い太陽 (シャドウ硬い)';
  return '真上 (フラット、コントラスト極大)';
}

// --------------------------------------------------
// Open-Meteo の WMO Weather Code をテキスト化
// --------------------------------------------------
function weatherCodeToText(code) {
  const map = {
    0: '快晴', 1: '晴れ', 2: '一部雲', 3: '曇り',
    45: '霧', 48: '霧氷',
    51: '霧雨 (弱)', 53: '霧雨 (中)', 55: '霧雨 (強)',
    61: '雨 (弱)', 63: '雨 (中)', 65: '雨 (強)',
    71: '雪 (弱)', 73: '雪 (中)', 75: '雪 (強)',
    77: '霰',
    80: 'にわか雨 (弱)', 81: 'にわか雨 (中)', 82: 'にわか雨 (強)',
    85: 'にわか雪 (弱)', 86: 'にわか雪 (強)',
    95: '雷雨', 96: '雷雨 + 霰', 99: '雷雨 + 大霰',
  };
  return map[code] || `コード ${code}`;
}
